import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Loader2, Lock } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [checkingSignup, setCheckingSignup] = useState(true);

  useEffect(() => {
    api.get("/settings/public")
      .then((r) => setSignupEnabled(String(r.data?.allow_signup ?? "true").toLowerCase() !== "false"))
      .catch(() => setSignupEnabled(true))
      .finally(() => setCheckingSignup(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await register({ name: name.trim(), email: email.trim(), password, role });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    const target = res.user.role === "teacher" ? "/teacher" : "/dashboard";
    nav(target);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <PrismLogo size={36} />
          <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
        </Link>

        <h1 className="font-display text-3xl font-black text-slate-900">Create your account</h1>
        <p className="mt-1.5 text-slate-500 text-sm">Sign up as a student or teacher — it only takes a minute.</p>

        {!checkingSignup && !signupEnabled ? (
          <div className="mt-8 qs-card p-8 text-center" data-testid="signup-disabled">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-amber-100 text-amber-700 grid place-items-center">
              <Lock className="w-6 h-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Sign-ups are currently closed</h2>
            <p className="mt-2 text-sm text-slate-500">
              Your administrator has disabled new registrations. Please ask them for an invitation.
            </p>
            <Link to="/login" className="btn-primary text-sm mt-6 inline-flex" data-testid="signup-disabled-login">
              Back to sign in
            </Link>
          </div>
        ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Full name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
              placeholder="Jamie Rivera"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
              placeholder="you@school.edu"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
              placeholder="At least 6 characters"
              data-testid="register-password-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">I am a…</label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              {["student", "teacher"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`px-4 py-3 rounded-xl border transition text-sm font-semibold ${
                    role === r
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid={`role-select-${r}`}
                >
                  {r === "student" ? "Student" : "Teacher"}
                </button>
              ))}
            </div>
          </div>
          {err && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="register-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-70" data-testid="register-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>
        )}

        <p className="mt-6 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-indigo-600 font-semibold hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
